import { Box, Card, CardContent, Container, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { CATEGORY_IMAGES } from "../assets/images";
import logoBCT from "../assets/images/logoBCT.png";
import NoData from "../components/common/NoData";
import SEO from "../components/seo/SEO";
import { getCityData } from "../utils/getCityData";
import slugify from "../utils/slugify";
import CategoryHeader from "./CategoryHeader";

const Category = () => {
  const { city, category } = useParams();
  const cityData = getCityData(city);
  if (!cityData || !cityData[category]) {
    return (
      <NoData
        title="No places found"
        description={`No ${category} available in this city yet.`}
        showContact
      />
    );
  }

  return (
    <>
      <SEO
        title={`${category} in ${cityData.name} | NearbyGuide`}
        description={`Find the best ${category} in ${cityData.name}, ${cityData.state}. View locations, timings, details and more.`}
        canonical={`https://nearbyguide.in/city/${city}/${category}`}
        image={CATEGORY_IMAGES[category]}
        key={`${category} in ${cityData.name}, best ${category} in ${cityData.name}, nearby ${category} in ${cityData.name}, ${category} near me in ${cityData.name}, popular ${category} in ${cityData.name}, top ${category} in ${cityData.name}, local ${category} in ${cityData.name}, famous ${category} in ${cityData.name}, ${category} locations in ${cityData.name}, ${category} timings in ${cityData.name}, ${category} details in ${cityData.name}, ${category} places in ${cityData.name}, ${cityData.name} ${category} guide, NearbyGuide ${category} ${cityData.name}`}
      />

      <CategoryHeader
        cityName={cityData.name}
        state={cityData.state}
        category={category}
      />

      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "24px",
          }}
        >
          {cityData[category].map((item) => (
            <Box
              key={item.name}
              sx={{
                flex: {
                  xs: "1 1 100%", // mobile: 1 card
                  sm: "1 1 calc(50% - 24px)", // tablet: 2 cards
                  md: "1 1 calc(33.333% - 24px)", // desktop: 3 cards
                  lg: "1 1 calc(25% - 24px)", // large: 4 cards
                },
                maxWidth: {
                  xs: "100%",
                  sm: "calc(50% - 24px)",
                  md: "calc(33.333% - 24px)",
                  lg: "calc(25% - 24px)",
                },
              }}
            >
              <PlaceCard
                item={item}
                category={category}
                link={`/city/${city}/${category}/${slugify(item.name)}`}
              />
            </Box>
          ))}
        </Box>
      </Container>
    </>
  );
};

export default Category;

const getInitials = (name) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const PlaceCard = ({ item, link, category }) => {
  const hasImage = Boolean(item.image);

  const fallbackImage = CATEGORY_IMAGES[category] || CATEGORY_IMAGES.tourist;

  const imageToShow = item.image || fallbackImage;

  return (
    <Card
      component={Link}
      to={link}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#ffff",
        border: "1px solid rgba(13,59,102,0.12)",
        fontFamily: "var(--font-primary)",
        transition: "all 0.35s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 18px 42px rgba(13,59,102,0.22)",
        },
      }}
    >
      {/* IMAGE / PLACEHOLDER */}
      <Box
        sx={{
          height: 160,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `url(${imageToShow})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(13,59,102,0.15), rgba(13,59,102,0.55))",
          }}
        />

        {/* Watermark Logo */}
        <Box
          component="img"
          src={logoBCT}
          alt="NearbyGuide"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 10,
            width: 75,
            opacity: 0.9,
          }}
        />

        {/* Initials */}
        {!hasImage && (
          <Typography
            sx={{
              position: "relative",
              fontSize: 42,
              fontWeight: 800,
              letterSpacing: 3,
              color: "var(--color-sunshine)",
            }}
          >
            {getInitials(item.name)}
          </Typography>
        )}
      </Box>

      {/* CONTENT */}
      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {/* Title */}
        <Typography
          sx={{
            fontSize: 17,
            fontWeight: 700,
            color: "var(--text-heading)",
            mb: 0.5,
            lineHeight: 1.3,
          }}
        >
          {item.name}
        </Typography>

        {/* Short Description (2-line clamp) */}
        <Typography
          sx={{
            fontSize: 14,
            color: "var(--text-secondary)",
            mb: 1.2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.area} • {item.details}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Timing */}
        {item.timing && (
          <Typography
            sx={{
              fontSize: 13,
              color: "var(--text-muted)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            ⏰ {item.timing}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
